'use memo';

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState, useTransition } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import NavigationCard, { type NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import { Text } from '@/components/Typography/Text';
import { orpc } from '@/lib/api/orpc';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { SettingsItemCard } from '../../../../../-components/SettingsItem';
import { MappingEditor } from './-components/MappingEditor';
import { MappingViewer } from './-components/MappingViewer';
import { RowFilterSection } from './-components/RowFilterSection';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/$id/_pipeline-detail/data-link-settings/')({
  component: OutboundFieldMappingsPage
});

/**
 * OUT FieldMapping 編集ページ。IN data-link-settings と同型の
 * 「左サイドバー（NavigationCard）+ 右に設定カード」レイアウト。
 * 左カラムの説明文は廃し、サイドバーで PEOPLE 種別セクションへジャンプする。
 */
function OutboundFieldMappingsPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline'
  });
  const {
    id: pipelineId
  } = Route.useParams();
  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  // 値変換設定（決定表）は列アンカーで列と結び付く独立リソース。列名を変えた行の表が
  // 迷子にならないよう、列定義と同じ「保存」でまとめて送る。編集開始時に別 query で取得する。
  const decisionTablesQuery = useQuery({
    ...orpc.outboundDataPipeline.listDecisionTables.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isStartEditPending, startTransition] = useTransition();
  const startEdit = useCallback(() => {
    startTransition(() => setIsEditing(true));
  }, []);
  const finishEdit = useCallback(() => setIsEditing(false), []);
  const isPeople = detailQuery.data?.basicInfo?.dataType === 'PEOPLE';
  // 取得失敗と読み込み中を分ける。`data == null` で束ねると、失敗しても「読み込み中」を
  // 出し続けて復帰できず、原因も再試行の手掛かりも画面に出ない。
  const mappingLoadFailed = detailQuery.isError || isEditing && decisionTablesQuery.isError;
  const decisionTables = decisionTablesQuery.data?.items;
  const retryMappingLoad = () => {
    if (detailQuery.isError) void detailQuery.refetch();
    if (decisionTablesQuery.isError) void decisionTablesQuery.refetch();
  };
  const navigationSections: NavigationSectionGroup[] = [{
    titleLabel: t('detail.dataLinkSettings.mapping.title'),
    to: '/settings/data-connector/outbound/data-pipeline/$id/data-link-settings',
    hash: 'mapping-settings'
  },
  // 行フィルタはメンバー種別ごとの設定だが、マッピングとは別のカードに置く（列の対応と行の
  // 取捨は別の判断で、1 カードに混ぜると保存の単位も混ざる）。
  // 人員の絞り込みなので PEOPLE 以外には出さない（出すと飛び先が空のカードになる）。
  ...(isPeople ? [{
    titleLabel: t('detail.dataLinkSettings.rowFilter.title'),
    to: '/settings/data-connector/outbound/data-pipeline/$id/data-link-settings' as const,
    hash: 'row-filter-settings'
  }] : [])];
  return <FillScreen>
      <HStack className="gap-9 px-12 py-6">
        <div className="sticky top-6 w-52 shrink-0">
          <NavigationCard sections={navigationSections} />
        </div>

        <VStack className="min-w-0 flex-1 gap-6">
          <SettingsItemCard id="mapping-settings" className={cn('overflow-visible', (isEditing || isStartEditPending) && 'ring-2 ring-primary-fill')}>
            {mappingLoadFailed ?
          // 取得失敗をローディング表示のままにしない。決定表は**集合置換**で保存するため、
          // 取れていない状態を空配列として編集させると保存時にサーバ側の表が全部消える。
          <VStack className="gap-2">
                <Text variant="p" className="text-error-on-neutral-fill">
                  {t('detail.dataLinkSettings.mapping.loadFailed')}
                </Text>
                <div>
                  <ActionButton variant="secondary" type="button" onClick={retryMappingLoad}>
                    {t('detail.dataLinkSettings.mapping.retry')}
                  </ActionButton>
                </div>
              </VStack> : detailQuery.data == null ? <Text variant="p" className="text-neutral-muted-on-fill">
                {t('loading')}
              </Text> : isEditing ?
          // 決定表が取れるまで編集を開かない。`?? []` で空集合として渡すと、保存が
          // 集合置換なのでサーバ側の表が全部消える。
          decisionTables == null ? <Text variant="p" className="text-neutral-muted-on-fill">
                  {t('loading')}
                </Text> : <MappingEditor pipelineId={pipelineId} mappings={detailQuery.data.fieldMappings} decisionTables={decisionTables} isPeople={isPeople} onDone={finishEdit} /> : <MappingViewer pipelineId={pipelineId} mappings={detailQuery.data.fieldMappings} isPeople={isPeople} isStartEditPending={isStartEditPending} onStartEdit={startEdit} />}
          </SettingsItemCard>

          {isPeople && <SettingsItemCard id="row-filter-settings">
              {detailQuery.data == null ? <Text variant="p" className="text-neutral-muted-on-fill">
                  {t('loading')}
                </Text> : <RowFilterSection pipelineId={pipelineId} />}
            </SettingsItemCard>}
        </VStack>
      </HStack>
    </FillScreen>;
}